
import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search } from "lucide-react";
import TablePagination from "@/components/masters/TablePagination";
import ReportLayout from "@/components/reports/ReportLayout";
import FilterCard from "@/components/reports/FilterCard";
import PaymentTrendsTable, { PaymentTrendItem } from "@/components/reports/PaymentTrendsTable";
import ReportSummaryHeader from "@/components/reports/ReportSummaryHeader";

// Mock payment trends data
const INITIAL_PAYMENT_DATA: PaymentTrendItem[] = [
  {
    id: 1,
    period: "Week 1",
    month: "May",
    year: "2025",
    cash: 48500,
    online: 67800,
    total: 116300,
    percentageChange: 0,
  },
  {
    id: 2,
    period: "Week 2",
    month: "May",
    year: "2025",
    cash: 51200,
    online: 72500,
    total: 123700,
    percentageChange: 6.4,
  },
  {
    id: 3,
    period: "Week 3",
    month: "May",
    year: "2025",
    cash: 49800,
    online: 76900,
    total: 126700,
    percentageChange: 2.4,
  },
  {
    id: 4,
    period: "Week 4",
    month: "May",
    year: "2025",
    cash: 47500,
    online: 81200,
    total: 128700,
    percentageChange: 1.6,
  },
  {
    id: 5,
    period: "Week 1",
    month: "April",
    year: "2025",
    cash: 46200,
    online: 62500,
    total: 108700,
    percentageChange: -2.5,
  },
  {
    id: 6,
    period: "Week 2",
    month: "April",
    year: "2025",
    cash: 49800,
    online: 66300,
    total: 116100,
    percentageChange: 6.8,
  },
  {
    id: 7,
    period: "Week 3",
    month: "April",
    year: "2025",
    cash: 51200,
    online: 69800,
    total: 121000,
    percentageChange: 4.2,
  },
  {
    id: 8,
    period: "Week 4",
    month: "April",
    year: "2025",
    cash: 52400,
    online: 72100,
    total: 124500,
    percentageChange: 2.9,
  },
];

const PaymentTrendsPage = () => {
  const [month, setMonth] = useState<string>("May");
  const [year, setYear] = useState<string>("2025");
  const [paymentData, setPaymentData] = useState(INITIAL_PAYMENT_DATA);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const itemsPerPage = 5;

  // Calculate totals
  const totalCash = paymentData.reduce((sum, item) => sum + item.cash, 0);
  const totalOnline = paymentData.reduce((sum, item) => sum + item.online, 0);
  const totalAmount = totalCash + totalOnline;
  const onlinePercentage = Math.round((totalOnline / totalAmount) * 100);
  const cashPercentage = 100 - onlinePercentage;

  // Filter by search query
  const filteredData = paymentData.filter((item) => {
    const searchString = searchQuery.toLowerCase();
    return (
      item.period.toLowerCase().includes(searchString) ||
      item.month.toLowerCase().includes(searchString) ||
      item.year.toLowerCase().includes(searchString)
    );
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const handleFilter = () => {
    // In a real app, this would filter based on the selected criteria
    // For this demo, we'll just shuffle the data to simulate filtering
    setPaymentData([...INITIAL_PAYMENT_DATA].sort(() => Math.random() - 0.5));
    setCurrentPage(1);
  };

  const handleReset = () => {
    setMonth("May");
    setYear("2025");
    setSearchQuery("");
    setPaymentData(INITIAL_PAYMENT_DATA);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <ReportLayout
      title="Payment Trends Report"
      description="Analyze payment method trends and patterns"
    >
      <FilterCard
        title="Report Filters"
        description="Set parameters to analyze payment trends"
        onReset={handleReset}
        onFilter={handleFilter}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="month">Month</Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger>
                <SelectValue placeholder="Select month" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="May">May</SelectItem>
                <SelectItem value="April">April</SelectItem>
                <SelectItem value="March">March</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="year">Year</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger>
                <SelectValue placeholder="Select year" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2023">2023</SelectItem>
                <SelectItem value="2024">2024</SelectItem>
                <SelectItem value="2025">2025</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="search">Search</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="search"
                type="text"
                placeholder="Search periods..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      </FilterCard>

      <Card>
        <CardHeader>
          <ReportSummaryHeader
            title="Payment Trends"
            description={`${month} ${year}`}
            totalAmount={totalAmount}
            additionalInfo={
              <div className="text-sm text-muted-foreground flex gap-2">
                <span className="text-blue-600">Online: {onlinePercentage}%</span>
                <span>•</span>
                <span className="text-green-600">Cash: {cashPercentage}%</span>
              </div>
            }
          />
        </CardHeader>
        <CardContent>
          <PaymentTrendsTable 
            data={filteredData} 
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
          />
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            className="mt-4"
          />
        </CardContent>
      </Card>
    </ReportLayout>
  );
};

export default PaymentTrendsPage;
